import React, { useState, useEffect } from 'react';
import { CreditCard, Wallet, Save, CheckCircle, AlertCircle, Eye, EyeOff, TestTube, Link, Trash2, Plus, Copy, ExternalLink, ArrowRightLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentGatewaySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showXenditKey, setShowXenditKey] = useState(false);
  const [showDuitkuKey, setShowDuitkuKey] = useState(false);
  const [isActive, setIsActive] = useState(false);  // Toggle untuk aktifkan payment gateway
  const [config, setConfig] = useState({
    active_gateway: 'xendit', // 'xendit' or 'duitku'
    is_active: false, // Master switch for payment gateway
    payment_channels: {
      // Virtual Account Banks
      BCA: false, // Requires special activation
      BRI: true,
      BNI: true,
      MANDIRI: true,
      PERMATA: true,
      BJB: true,
      BSI: true,
      CIMB: true,
      SAHABAT_SAMPOERNA: false,
      // Retail Outlets
      ALFAMART: true,
      INDOMARET: true,
      // E-Wallets
      OVO: true,
      DANA: true,
      LINKAJA: true,
      SHOPEEPAY: true,
      ASTRAPAY: true,
      // Cards
      CREDIT_CARD: false, // Requires special activation
      // QR Code
      QRIS: true
    },
    webhook_forwarder: {
      enabled: false,
      forward_urls: []
    },
    xendit: {
      api_key: '',
      webhook_token: '',
      environment: 'development'
    },
    duitku: {
      merchant_code: '',
      api_key: '',
      callback_url: '',
      return_url: '',
      environment: 'sandbox'
    },
    tripay: {
      merchant_code: '',
      api_key: '',
      private_key: '',
      environment: 'production'
    }
  });
  const [showTripayKey, setShowTripayKey] = useState(false);
  const [showTripayPriv, setShowTripayPriv] = useState(false);

  // TriPay-specific channels (matches TriPay merchant API codes)
  const tripayPaymentChannels = {
    banks: [
      { code: 'BRIVA', name: 'BRI Virtual Account' },
      { code: 'BNIVA', name: 'BNI Virtual Account' },
      { code: 'MANDIRIVA', name: 'Mandiri Virtual Account' },
      { code: 'BCAVA', name: 'BCA Virtual Account' },
      { code: 'PERMATAVA', name: 'Permata Virtual Account' },
      { code: 'CIMBVA', name: 'CIMB Niaga Virtual Account' },
      { code: 'DANAMONVA', name: 'Danamon Virtual Account' },
      { code: 'BSIVA', name: 'BSI Virtual Account' },
      { code: 'MUAMALATVA', name: 'Muamalat Virtual Account' },
      { code: 'BSSVA', name: 'Bank Sahabat Sampoerna VA' },
      { code: 'OCBCVA', name: 'OCBC NISP Virtual Account' },
      { code: 'OTHERBANKVA', name: 'Bank Lain (Virtual Account)' },
    ],
    ewallets: [
      { code: 'DANA', name: 'DANA' },
      { code: 'OVO', name: 'OVO' },
      { code: 'LINKAJA', name: 'LinkAja' },
      { code: 'SHOPEEPAY', name: 'ShopeePay' },
      { code: 'TRUEMONEY', name: 'TrueMoney' },
    ],
    qr: [
      { code: 'QRIS', name: 'QRIS' },
      { code: 'QRIS2', name: 'QRIS by BRI' },
      { code: 'QRISC', name: 'QRIS Custom' },
      { code: 'QRIS_SHOPEEPAY', name: 'QRIS Custom by ShopeePay' },
    ],
    retail: [
      { code: 'ALFAMART', name: 'Alfamart' },
      { code: 'INDOMARET', name: 'Indomaret' },
      { code: 'ALFAMIDI', name: 'Alfamidi' },
    ],
  };

  const toggleTripayChannel = (code) => {
    setConfig(prev => ({
      ...prev,
      tripay_channels: {
        ...(prev.tripay_channels || {}),
        [code]: !(prev.tripay_channels || {})[code],
      },
    }));
  };

  // TRIPAY_ACTIVE_SYNC_V1 - fetch active channel list from TriPay merchant API and populate checkboxes
  const [syncingTripay, setSyncingTripay] = useState(false);
  const syncTripayFromMerchant = async () => {
    if (syncingTripay) return;
    try {
      setSyncingTripay(true);
      toast.info('Mengambil daftar channel aktif dari TriPay...');
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/api/v2/payments/tripay/merchant-channels`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const activeCodes = new Set((r.data?.active_codes || []).map(c => String(c).toUpperCase()));
      const next = {};
      const ourCatalogCodes = new Set();
      Object.values(tripayPaymentChannels).forEach(list => list.forEach(c => {
        ourCatalogCodes.add(String(c.code).toUpperCase());
        if (activeCodes.has(String(c.code).toUpperCase())) next[c.code] = true;
      }));
      const enabledCount = Object.keys(next).length;
      const skippedFromApi = (r.data?.active_codes || []).filter(c => !ourCatalogCodes.has(String(c).toUpperCase()));
      setConfig(prev => ({ ...prev, tripay_channels: next }));
      toast.success(
        `Sync selesai: ${enabledCount} channel aktif dicentang.` +
        (skippedFromApi.length ? ` (${skippedFromApi.length} channel TriPay belum di katalog: ${skippedFromApi.join(', ')})` : '')
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal sync channel dari TriPay');
    } finally {
      setSyncingTripay(false);
    }
  };

  const [newForwardUrl, setNewForwardUrl] = useState('');

  // Payment channel definitions with logos
  const paymentChannels = {
    banks: [
      { code: 'BCA', name: 'Bank BCA', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5c/Bank_Central_Asia.svg/120px-Bank_Central_Asia.svg.png' },
      { code: 'BRI', name: 'Bank BRI', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/68/BANK_BRI_logo.svg/120px-BANK_BRI_logo.svg.png' },
      { code: 'BNI', name: 'Bank BNI', logo: 'https://upload.wikimedia.org/wikipedia/id/thumb/5/55/BNI_logo.svg/120px-BNI_logo.svg.png' },
      { code: 'MANDIRI', name: 'Bank Mandiri', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/ad/Bank_Mandiri_logo_2016.svg/120px-Bank_Mandiri_logo_2016.svg.png' },
      { code: 'PERMATA', name: 'Bank Permata', logo: 'https://upload.wikimedia.org/wikipedia/id/thumb/0/0f/PermataBank_logo.svg/120px-PermataBank_logo.svg.png' },
      { code: 'BJB', name: 'Bank BJB', logo: 'https://upload.wikimedia.org/wikipedia/id/thumb/d/d8/Bank_BJB_Logo.svg/120px-Bank_BJB_Logo.svg.png' },
      { code: 'BSI', name: 'Bank BSI', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a0/Bank_Syariah_Indonesia.svg/120px-Bank_Syariah_Indonesia.svg.png' },
      { code: 'CIMB', name: 'Bank CIMB Niaga', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e9/CIMB_Niaga_logo.svg/120px-CIMB_Niaga_logo.svg.png' },
      { code: 'SAHABAT_SAMPOERNA', name: 'Bank Sahabat Sampoerna', logo: null },
    ],
    retail: [
      { code: 'ALFAMART', name: 'Alfamart', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9d/Alfamart_logo.svg/120px-Alfamart_logo.svg.png' },
      { code: 'INDOMARET', name: 'Indomaret', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9b/Logo_Indomaret.svg/120px-Logo_Indomaret.svg.png' },
    ],
    ewallets: [
      { code: 'OVO', name: 'OVO', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/eb/Logo_ovo_purple.svg/120px-Logo_ovo_purple.svg.png' },
      { code: 'DANA', name: 'DANA', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/72/Logo_dana_blue.svg/120px-Logo_dana_blue.svg.png' },
      { code: 'LINKAJA', name: 'LinkAja', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/85/LinkAja.svg/120px-LinkAja.svg.png' },
      { code: 'SHOPEEPAY', name: 'ShopeePay', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/eb/Shopee-logo.png/120px-Shopee-logo.png' },
      { code: 'ASTRAPAY', name: 'AstraPay', logo: null },
    ],
    cards: [
      { code: 'CREDIT_CARD', name: 'Kartu Kredit/Debit', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/80px-Visa_Inc._logo.svg.png' },
    ],
    qr: [
      { code: 'QRIS', name: 'QRIS', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a2/Logo_QRIS.svg/120px-Logo_QRIS.svg.png' },
    ]
  };

  const toggleChannel = (code) => {
    setConfig(prev => ({
      ...prev,
      payment_channels: {
        ...prev.payment_channels,
        [code]: !prev.payment_channels[code]
      }
    }));
  };

  const toggleAllChannels = (checked) => {
    const allChannels = {};
    Object.values(paymentChannels).flat().forEach(ch => {
      allChannels[ch.code] = checked;
    });
    setConfig(prev => ({
      ...prev,
      payment_channels: allChannels
    }));
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/payment-gateway/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setConfig(prev => ({
          ...prev,
          ...response.data.data,
          webhook_forwarder: response.data.data.webhook_forwarder || { enabled: false, forward_urls: [] }
        }));
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Gagal memuat konfigurasi');
    } finally {
      setLoading(false);
    }
  };

  const addForwardUrl = () => {
    if (!newForwardUrl) return;
    if (!newForwardUrl.startsWith('http')) {
      toast.error('URL harus dimulai dengan http:// atau https://');
      return;
    }
    setConfig(prev => ({
      ...prev,
      webhook_forwarder: {
        ...prev.webhook_forwarder,
        forward_urls: [
          ...prev.webhook_forwarder.forward_urls,
          { url: newForwardUrl, enabled: true }
        ]
      }
    }));
    setNewForwardUrl('');
  };

  const removeForwardUrl = (index) => {
    setConfig(prev => ({
      ...prev,
      webhook_forwarder: {
        ...prev.webhook_forwarder,
        forward_urls: prev.webhook_forwarder.forward_urls.filter((_, i) => i !== index)
      }
    }));
  };

  const toggleForwardUrlEnabled = (index) => {
    setConfig(prev => ({
      ...prev,
      webhook_forwarder: {
        ...prev.webhook_forwarder,
        forward_urls: prev.webhook_forwarder.forward_urls.map((item, i) => 
          i === index ? { ...item, enabled: !item.enabled } : item
        )
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/api/v2/payment-gateway/config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success('Konfigurasi berhasil disimpan');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan konfigurasi');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (gateway) => {
    try {
      const token = localStorage.getItem('token');
      toast.info(`Testing ${gateway} connection...`);
      
      // Send API key for testing
      const testData = {};
      if (gateway === 'xendit' && config.xendit?.api_key) {
        testData.api_key = config.xendit.api_key;
      } else if (gateway === 'duitku' && config.duitku?.api_key) {
        testData.api_key = config.duitku.api_key;
        testData.merchant_code = config.duitku.merchant_code;
      } else if (gateway === 'tripay') {
        testData.api_key = config.tripay.api_key;
        testData.merchant_code = config.tripay.merchant_code;
        testData.private_key = config.tripay.private_key;
        testData.environment = config.tripay.environment;
      }
      
      const response = await axios.post(`${API_URL}/api/v2/payment-gateway/test/${gateway}`, testData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(`${gateway} connection successful!`);
      }
    } catch (error) {
      console.error(`Error testing ${gateway}:`, error);
      toast.error(error.response?.data?.detail || `Failed to test ${gateway} connection`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4 dark:bg-slate-700"></div>
          <div className="h-64 bg-slate-200 rounded dark:bg-slate-700"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
            Payment Gateway Settings
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Konfigurasi integrasi payment gateway untuk pembayaran invoice
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Active/Inactive Toggle */}
          <div className="flex items-center gap-3 px-4 py-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
            <span className={`text-sm font-medium ${config.is_active ? 'text-green-600' : 'text-gray-500'}`}>
              {config.is_active ? 'Aktif' : 'Tidak Aktif'}
            </span>
            <button
              onClick={() => setConfig({ ...config, is_active: !config.is_active })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                config.is_active ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.is_active ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Menyimpan...' : 'Simpan'}
          </Button>
        </div>
      </div>

      {/* Status Badge */}
      {config.is_active ? (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
          Payment Gateway Aktif - Pelanggan dapat melakukan pembayaran online
        </div>
      ) : (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
          <span className="w-2 h-2 rounded-full bg-gray-400"></span>
          Payment Gateway Tidak Aktif - Pembayaran online dinonaktifkan
        </div>
      )}

      <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-900/20">
        <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <AlertDescription className="text-amber-800 dark:text-amber-200">
          API Keys dan credentials sensitif akan disimpan dengan aman. Jangan bagikan ke pihak yang tidak berwenang.
        </AlertDescription>
      </Alert>

      {/* Active Gateway Selection */}
      <Card className="border-2 border-indigo-200 dark:border-indigo-800 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            Payment Gateway Aktif
          </CardTitle>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Pilih payment gateway yang akan digunakan untuk pembayaran pelanggan
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Tunai Option */}
            <button
              type="button"
              onClick={() => setConfig({ ...config, active_gateway: 'cash' })}
              className={`relative p-5 rounded-2xl border-2 transition-all duration-300 group ${
                config.active_gateway === 'cash'
                  ? 'border-amber-500 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 shadow-lg shadow-amber-100'
                  : 'border-slate-200 dark:border-slate-700 hover:border-amber-300 hover:shadow-md bg-white dark:bg-slate-800'
              }`}
            >
              {config.active_gateway === 'cash' && (
                <div className="absolute top-3 right-3">
                  <CheckCircle className="w-6 h-6 text-amber-500" />
                </div>
              )}
              <div className="flex flex-col items-center text-center">
                <div className={`p-4 rounded-2xl mb-3 transition-all ${
                  config.active_gateway === 'cash'
                    ? 'bg-gradient-to-br from-amber-500 to-orange-500 shadow-lg'
                    : 'bg-amber-100 dark:bg-amber-900/40 group-hover:bg-amber-200'
                }`}>
                  <Wallet className={`w-8 h-8 ${config.active_gateway === 'cash' ? 'text-white' : 'text-amber-600'}`} />
                </div>
                <h3 className="font-bold text-lg text-slate-800 dark:text-white mb-1">Tunai</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">Pembayaran manual / cash</p>
              </div>
            </button>

            {/* Xendit Option */}
            <button
              type="button"
              onClick={() => setConfig({ ...config, active_gateway: 'xendit' })}
              className={`relative p-5 rounded-2xl border-2 transition-all duration-300 group ${
                config.active_gateway === 'xendit'
                  ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 shadow-lg shadow-blue-100'
                  : 'border-slate-200 dark:border-slate-700 hover:border-blue-300 hover:shadow-md bg-white dark:bg-slate-800'
              }`}
            >
              {config.active_gateway === 'xendit' && (
                <div className="absolute top-3 right-3">
                  <CheckCircle className="w-6 h-6 text-blue-500" />
                </div>
              )}
              <div className="flex flex-col items-center text-center">
                <div className={`p-4 rounded-2xl mb-3 transition-all ${
                  config.active_gateway === 'xendit'
                    ? 'bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg'
                    : 'bg-blue-100 dark:bg-blue-900/40 group-hover:bg-blue-200'
                }`}>
                  <CreditCard className={`w-8 h-8 ${config.active_gateway === 'xendit' ? 'text-white' : 'text-blue-600'}`} />
                </div>
                <h3 className="font-bold text-lg text-slate-800 dark:text-white mb-1">Xendit</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">VA, QRIS, E-Wallet, Credit Card</p>
              </div>
            </button>

            {/* Duitku Option */}
            <button
              type="button"
              onClick={() => setConfig({ ...config, active_gateway: 'duitku' })}
              className={`relative p-5 rounded-2xl border-2 transition-all duration-300 group ${
                config.active_gateway === 'duitku'
                  ? 'border-emerald-500 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30 shadow-lg shadow-emerald-100'
                  : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300 hover:shadow-md bg-white dark:bg-slate-800'
              }`}
            >
              {config.active_gateway === 'duitku' && (
                <div className="absolute top-3 right-3">
                  <CheckCircle className="w-6 h-6 text-emerald-500" />
                </div>
              )}
              <div className="flex flex-col items-center text-center">
                <div className={`p-4 rounded-2xl mb-3 transition-all ${
                  config.active_gateway === 'duitku'
                    ? 'bg-gradient-to-br from-emerald-500 to-green-600 shadow-lg'
                    : 'bg-emerald-100 dark:bg-emerald-900/40 group-hover:bg-emerald-200'
                }`}>
                  <Wallet className={`w-8 h-8 ${config.active_gateway === 'duitku' ? 'text-white' : 'text-emerald-600'}`} />
                </div>
                <h3 className="font-bold text-lg text-slate-800 dark:text-white mb-1">Duitku</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">VA, QRIS, E-Wallet, Retail</p>
              </div>
            </button>

            {/* TriPay Option */}
            <button
              type="button"
              data-testid="gateway-card-tripay"
              onClick={() => setConfig({ ...config, active_gateway: 'tripay' })}
              className={`relative p-5 rounded-2xl border-2 transition-all duration-300 group ${
                config.active_gateway === 'tripay'
                  ? 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 shadow-lg shadow-indigo-100'
                  : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300 hover:shadow-md bg-white dark:bg-slate-800'
              }`}
            >
              {config.active_gateway === 'tripay' && (
                <div className="absolute top-3 right-3">
                  <CheckCircle className="w-6 h-6 text-indigo-500" />
                </div>
              )}
              <div className="flex flex-col items-center text-center">
                <div className={`p-4 rounded-2xl mb-3 transition-all ${
                  config.active_gateway === 'tripay'
                    ? 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg'
                    : 'bg-indigo-100 dark:bg-indigo-900/40 group-hover:bg-indigo-200'
                }`}>
                  <CreditCard className={`w-8 h-8 ${config.active_gateway === 'tripay' ? 'text-white' : 'text-indigo-600'}`} />
                </div>
                <h3 className="font-bold text-lg text-slate-800 dark:text-white mb-1">TriPay</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">VA, QRIS, E-Wallet, Retail</p>
              </div>
            </button>
          </div>
          
          {/* Active Gateway Status */}
          <div className="mt-6 p-4 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  config.active_gateway === 'cash' ? 'bg-amber-100 text-amber-600' :
                  config.active_gateway === 'xendit' ? 'bg-blue-100 text-blue-600' : 'bg-emerald-100 text-emerald-600'
                }`}>
                  {config.active_gateway === 'cash' ? <Wallet className="w-5 h-5" /> : <CreditCard className="w-5 h-5" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Gateway Aktif</p>
                  <p className={`text-lg font-bold ${
                    config.active_gateway === 'cash' ? 'text-amber-600' :
                    config.active_gateway === 'xendit' ? 'text-blue-600' : 'text-emerald-600'
                  }`}>
                    {config.active_gateway === 'cash' ? 'Tunai' :
                     config.active_gateway === 'xendit' ? 'Xendit' : 'Duitku'}
                  </p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                config.active_gateway === 'cash' ? 'bg-amber-100 text-amber-700' :
                config.active_gateway === 'xendit' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'
              }`}>
                Active
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* TriPay Channel Selection */}
      {config.active_gateway === 'tripay' && (
        <Card className="border-2 border-indigo-200 dark:border-indigo-800" data-testid="tripay-channels-card">
          <CardHeader className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white text-xl flex items-center gap-2"><CreditCard className="w-6 h-6" />Channel Pembayaran TriPay</CardTitle>
                <p className="text-indigo-100 text-xs mt-1">Kode channel sesuai TriPay Merchant API</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" className="text-slate-800 bg-white hover:bg-slate-100" data-testid="tripay-sync-from-merchant"
                        onClick={syncTripayFromMerchant} disabled={syncingTripay}>
                  {syncingTripay ? 'Sinkronisasi...' : 'Sync dari TriPay'}
                </Button>
                <Button size="sm" variant="secondary" className="text-slate-800" data-testid="tripay-select-all"
                        onClick={() => { const all = {}; Object.values(tripayPaymentChannels).forEach(list => list.forEach(c => { all[c.code] = true; })); setConfig(prev => ({ ...prev, tripay_channels: all })); }}>Pilih Semua</Button>
                <Button size="sm" variant="secondary" className="text-slate-800" data-testid="tripay-clear-all"
                        onClick={() => setConfig(prev => ({ ...prev, tripay_channels: {} }))}>Hapus Semua</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {[
              { title: 'Virtual Account', dot: 'bg-blue-500', list: tripayPaymentChannels.banks, active: 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30', hover: 'hover:border-indigo-300' },
              { title: 'E-Wallet', dot: 'bg-emerald-500', list: tripayPaymentChannels.ewallets, active: 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30', hover: 'hover:border-emerald-300' },
              { title: 'QRIS', dot: 'bg-orange-500', list: tripayPaymentChannels.qr, active: 'border-orange-400 bg-orange-50 dark:bg-orange-900/30', hover: 'hover:border-orange-300' },
              { title: 'Retail Outlet', dot: 'bg-red-500', list: tripayPaymentChannels.retail, active: 'border-red-400 bg-red-50 dark:bg-red-900/30', hover: 'hover:border-red-300' },
            ].map(section => (
              <div key={section.title}>
                <h4 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-3 flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${section.dot}`}></span>{section.title}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {section.list.map(ch => (
                    <label key={ch.code}
                           className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                             config.tripay_channels?.[ch.code] ? section.active : `border-slate-200 dark:border-slate-700 ${section.hover}`
                           }`}>
                      <input type="checkbox" data-testid={`tripay-ch-${ch.code}`}
                             checked={config.tripay_channels?.[ch.code] || false}
                             onChange={() => toggleTripayChannel(ch.code)} />
                      <span className="text-sm font-medium">{ch.name}</span>
                      <code className="ml-auto text-[10px] text-slate-400">{ch.code}</code>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Payment Channels Selection (Duitku/Xendit generic - hidden when TriPay active) */}
      {config.active_gateway !== 'tripay' && (
      <Card className="border-2 border-emerald-200 dark:border-emerald-800">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                Channel Pembayaran
              </CardTitle>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                Pilih metode pembayaran yang akan ditampilkan ke pelanggan
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => toggleAllChannels(true)}
                className="text-xs"
              >
                Pilih Semua
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => toggleAllChannels(false)}
                className="text-xs"
              >
                Hapus Semua
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Virtual Account Banks */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              Virtual Account (Bank Transfer)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {paymentChannels.banks.map((channel) => (
                <label
                  key={channel.code}
                  className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                    config.payment_channels?.[channel.code]
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={config.payment_channels?.[channel.code] || false}
                    onChange={() => toggleChannel(channel.code)}
                    className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500 dark:text-blue-400 dark:border-slate-600"
                  />
                  {channel.logo ? (
                    <img src={channel.logo} alt={channel.name} className="h-5 w-auto object-contain" onError={(e) => e.target.style.display='none'} />
                  ) : null}
                  <span className="text-sm text-slate-700 dark:text-slate-300">{channel.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Retail Outlets */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
              Retail Outlet
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {paymentChannels.retail.map((channel) => (
                <label
                  key={channel.code}
                  className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                    config.payment_channels?.[channel.code]
                      ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={config.payment_channels?.[channel.code] || false}
                    onChange={() => toggleChannel(channel.code)}
                    className="w-4 h-4 text-orange-600 rounded border-slate-300 focus:ring-orange-500 dark:text-orange-400 dark:border-slate-600"
                  />
                  {channel.logo ? (
                    <img src={channel.logo} alt={channel.name} className="h-5 w-auto object-contain" onError={(e) => e.target.style.display='none'} />
                  ) : null}
                  <span className="text-sm text-slate-700 dark:text-slate-300">{channel.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* E-Wallets */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
              E-Wallet
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {paymentChannels.ewallets.map((channel) => (
                <label
                  key={channel.code}
                  className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                    config.payment_channels?.[channel.code]
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={config.payment_channels?.[channel.code] || false}
                    onChange={() => toggleChannel(channel.code)}
                    className="w-4 h-4 text-purple-600 rounded border-slate-300 focus:ring-purple-500 dark:text-purple-400 dark:border-slate-600"
                  />
                  {channel.logo ? (
                    <img src={channel.logo} alt={channel.name} className="h-5 w-auto object-contain" onError={(e) => e.target.style.display='none'} />
                  ) : null}
                  <span className="text-sm text-slate-700 dark:text-slate-300">{channel.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Credit/Debit Cards */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
              Kartu Kredit/Debit
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {paymentChannels.cards.map((channel) => (
                <label
                  key={channel.code}
                  className={`flex flex-col p-3 rounded-lg border cursor-pointer transition-all ${
                    config.payment_channels?.[channel.code]
                      ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={config.payment_channels?.[channel.code] || false}
                      onChange={() => toggleChannel(channel.code)}
                      className="w-4 h-4 text-red-600 rounded border-slate-300 focus:ring-red-500 dark:text-red-400 dark:border-slate-600"
                    />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{channel.name}</span>
                  </div>
                  <div className="flex items-center gap-2 ml-6">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/60px-Visa_Inc._logo.svg.png" alt="Visa" className="h-4 w-auto" onError={(e) => e.target.style.display='none'} />
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/40px-Mastercard-logo.svg.png" alt="Mastercard" className="h-4 w-auto" onError={(e) => e.target.style.display='none'} />
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/American_Express_logo_%282018%29.svg/40px-American_Express_logo_%282018%29.svg.png" alt="Amex" className="h-4 w-auto" onError={(e) => e.target.style.display='none'} />
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/4/40/JCB_logo.svg/40px-JCB_logo.svg.png" alt="JCB" className="h-4 w-auto" onError={(e) => e.target.style.display='none'} />
                  </div>
                </label>
              ))}
            </div>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
              ⚠️ Kartu Kredit memerlukan verifikasi bisnis tambahan di Xendit
            </p>
          </div>

          {/* QR Payment */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              QR Payment
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {paymentChannels.qr.map((channel) => (
                <label
                  key={channel.code}
                  className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                    config.payment_channels?.[channel.code]
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={config.payment_channels?.[channel.code] || false}
                    onChange={() => toggleChannel(channel.code)}
                    className="w-4 h-4 text-green-600 rounded border-slate-300 focus:ring-green-500 dark:text-green-400 dark:border-slate-600"
                  />
                  {channel.logo ? (
                    <img src={channel.logo} alt={channel.name} className="h-5 w-auto object-contain" onError={(e) => e.target.style.display='none'} />
                  ) : null}
                  <span className="text-sm text-slate-700 dark:text-slate-300">{channel.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Info */}
          <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-900/20">
            <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-blue-800 dark:text-blue-200 text-sm">
              Channel yang dipilih harus sudah diaktifkan di dashboard Xendit/Duitku. Channel yang tidak aktif di dashboard tidak akan muncul.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
      )}

      {/* Webhook Forwarder */}
      <Card className="border-2 border-purple-200 dark:border-purple-800">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                Webhook Forwarder (Multi-App)
              </CardTitle>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                Forward webhook Xendit ke beberapa aplikasi sekaligus (untuk migrasi)
              </p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.webhook_forwarder?.enabled || false}
                onChange={(e) => setConfig(prev => ({
                  ...prev,
                  webhook_forwarder: { ...prev.webhook_forwarder, enabled: e.target.checked }
                }))}
                className="w-5 h-5 text-purple-600 rounded dark:text-purple-400"
              />
              <span className="text-sm font-medium">Aktifkan</span>
            </label>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Forwarder URL */}
          <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <p className="text-sm font-medium text-purple-800 dark:text-purple-200 mb-2">
              URL Webhook Forwarder (Gunakan URL ini di Xendit Dashboard):
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 p-2 bg-white dark:bg-slate-800 rounded border text-sm font-mono break-all">
                {API_URL}/api/payments/xendit/webhook-forwarder
              </code>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(`${API_URL}/api/payments/xendit/webhook-forwarder`);
                  toast.success('URL disalin!');
                }}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Forward URLs List */}
          <div>
            <Label className="mb-2 block">URL Tujuan Forward (Billing Lama, dll):</Label>
            <div className="space-y-2">
              {config.webhook_forwarder?.forward_urls?.map((item, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <input
                    type="checkbox"
                    checked={item.enabled}
                    onChange={() => toggleForwardUrlEnabled(index)}
                    className="w-4 h-4 text-purple-600 rounded dark:text-purple-400"
                  />
                  <code className="flex-1 text-sm font-mono text-slate-700 dark:text-slate-300 break-all">
                    {item.url}
                  </code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeForwardUrl(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              
              {(!config.webhook_forwarder?.forward_urls || config.webhook_forwarder.forward_urls.length === 0) && (
                <p className="text-sm text-slate-500 italic p-2 dark:text-slate-400">Belum ada URL forward ditambahkan</p>
              )}
            </div>
          </div>

          {/* Add New URL */}
          <div className="flex gap-2">
            <Input
              type="url"
              value={newForwardUrl}
              onChange={(e) => setNewForwardUrl(e.target.value)}
              placeholder="https://billing-lama.com/api/xendit/callback"
              className="flex-1"
            />
            <Button
              type="button"
              onClick={addForwardUrl}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              Tambah
            </Button>
          </div>

          <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-900/20">
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <AlertDescription className="text-amber-800 dark:text-amber-200 text-sm">
              <strong>Cara Migrasi:</strong>
              <ol className="list-decimal ml-4 mt-1 space-y-1">
                <li>Tambahkan URL webhook billing lama di atas</li>
                <li>Copy URL Webhook Forwarder dan paste di Xendit Dashboard</li>
                <li>Webhook akan diterima RadiusLink dan diteruskan ke billing lama</li>
                <li>Setelah migrasi selesai, nonaktifkan forwarder</li>
              </ol>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Xendit Configuration */}
        <Card className="border-2 border-blue-200 dark:border-blue-800 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-white/20 backdrop-blur rounded-xl">
                  <CreditCard className="w-7 h-7 text-white" />
                </div>
                <div>
                  <CardTitle className="text-white text-xl">Xendit</CardTitle>
                  <p className="text-blue-100 text-xs mt-0.5">Payment Gateway Indonesia</p>
                </div>
              </div>
              <div className="px-3 py-1 bg-white/20 rounded-full">
                <span className="text-xs font-medium">Recommended</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {/* API Key */}
            <div>
              <Label className="text-slate-700 font-medium dark:text-slate-200">API Key *</Label>
              <div className="relative mt-1.5">
                <Input
                  type={showXenditKey ? 'text' : 'password'}
                  value={config.xendit.api_key}
                  onChange={(e) => setConfig({
                    ...config,
                    xendit: { ...config.xendit, api_key: e.target.value }
                  })}
                  placeholder="xnd_development_..."
                  className="pr-10 border-slate-300 focus:border-blue-500 focus:ring-blue-500 dark:border-slate-600"
                />
                <button
                  type="button"
                  onClick={() => setShowXenditKey(!showXenditKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:text-slate-500"
                >
                  {showXenditKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1 dark:text-slate-400">
                <ExternalLink className="w-3 h-3" />
                Get from <a href="https://dashboard.xendit.co/settings/developers#api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium dark:text-blue-400">Xendit Dashboard</a>
              </p>
            </div>

            {/* Webhook Token */}
            <div>
              <Label className="text-slate-700 font-medium dark:text-slate-200">Webhook Verification Token</Label>
              <Input
                type="text"
                value={config.xendit.webhook_token}
                onChange={(e) => setConfig({
                  ...config,
                  xendit: { ...config.xendit, webhook_token: e.target.value }
                })}
                placeholder="Webhook token..."
                className="mt-1.5 border-slate-300 focus:border-blue-500 focus:ring-blue-500 dark:border-slate-600"
              />
              <p className="text-xs text-slate-500 mt-1.5 dark:text-slate-400">
                Optional - untuk verifikasi webhook callback
              </p>
            </div>

            {/* Environment */}
            <div>
              <Label className="text-slate-700 font-medium dark:text-slate-200">Environment</Label>
              <select
                value={config.xendit.environment}
                onChange={(e) => setConfig({
                  ...config,
                  xendit: { ...config.xendit, environment: e.target.value }
                })}
                className="w-full mt-1.5 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:border-slate-600 dark:bg-slate-800"
              >
                <option value="development">🧪 Development (Test Mode)</option>
                <option value="production">🚀 Production (Live Mode)</option>
              </select>
            </div>

            <Button
              onClick={() => handleTestConnection('xendit')}
              variant="outline"
              size="sm"
              className="w-full mt-2 border-blue-300 text-blue-600 hover:bg-blue-50 hover:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-900/30"
            >
              <TestTube className="w-4 h-4 mr-2" />
              Test Connection
            </Button>
          </CardContent>
        </Card>

        {/* Duitku Configuration */}
        <Card className="border-2 border-emerald-200 dark:border-emerald-800 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-emerald-500 to-green-600 text-white pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-white/20 backdrop-blur rounded-xl">
                  <Wallet className="w-7 h-7 text-white" />
                </div>
                <div>
                  <CardTitle className="text-white text-xl">Duitku</CardTitle>
                  <p className="text-emerald-100 text-xs mt-0.5">Payment Gateway Indonesia</p>
                </div>
              </div>
              <div className="px-3 py-1 bg-white/20 rounded-full">
                <span className="text-xs font-medium">Alternative</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {/* Merchant Code */}
            <div>
              <Label className="text-slate-700 font-medium dark:text-slate-200">Merchant Code *</Label>
              <Input
                type="text"
                value={config.duitku.merchant_code}
                onChange={(e) => setConfig({
                  ...config,
                  duitku: { ...config.duitku, merchant_code: e.target.value }
                })}
                placeholder="D1234"
                className="mt-1.5 border-slate-300 focus:border-emerald-500 focus:ring-emerald-500 dark:border-slate-600"
              />
              <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1 dark:text-slate-400">
                <ExternalLink className="w-3 h-3" />
                Get from <a href="https://passport.duitku.com" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline font-medium dark:text-emerald-400">Duitku Dashboard</a>
              </p>
            </div>

            {/* API Key */}
            <div>
              <Label className="text-slate-700 font-medium dark:text-slate-200">API Key *</Label>
              <div className="relative mt-1.5">
                <Input
                  type={showDuitkuKey ? 'text' : 'password'}
                  value={config.duitku.api_key}
                  onChange={(e) => setConfig({
                    ...config,
                    duitku: { ...config.duitku, api_key: e.target.value }
                  })}
                  placeholder="API Key..."
                  className="pr-10 border-slate-300 focus:border-emerald-500 focus:ring-emerald-500 dark:border-slate-600"
                />
                <button
                  type="button"
                  onClick={() => setShowDuitkuKey(!showDuitkuKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:text-slate-500"
                >
                  {showDuitkuKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Callback URL */}
            <div>
              <Label className="text-slate-700 font-medium dark:text-slate-200">Callback URL</Label>
              <Input
                type="text"
                value={config.duitku.callback_url}
                onChange={(e) => setConfig({
                  ...config,
                  duitku: { ...config.duitku, callback_url: e.target.value }
                })}
                placeholder="https://yourdomain.com/api/payments/duitku/callback"
                className="mt-1.5 border-slate-300 focus:border-emerald-500 focus:ring-emerald-500 dark:border-slate-600"
              />
            </div>

            {/* Return URL */}
            <div>
              <Label className="text-slate-700 font-medium dark:text-slate-200">Return URL</Label>
              <Input
                type="text"
                value={config.duitku.return_url}
                onChange={(e) => setConfig({
                  ...config,
                  duitku: { ...config.duitku, return_url: e.target.value }
                })}
                placeholder="https://yourdomain.com/payment/success"
                className="mt-1.5 border-slate-300 focus:border-emerald-500 focus:ring-emerald-500 dark:border-slate-600"
              />
            </div>

            {/* Environment */}
            <div>
              <Label className="text-slate-700 font-medium dark:text-slate-200">Environment</Label>
              <select
                value={config.duitku.environment}
                onChange={(e) => setConfig({
                  ...config,
                  duitku: { ...config.duitku, environment: e.target.value }
                })}
                className="w-full mt-1.5 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:border-slate-600 dark:bg-slate-800"
              >
                <option value="sandbox">🧪 Sandbox (Test Mode)</option>
                <option value="production">🚀 Production (Live Mode)</option>
              </select>
            </div>

            <Button
              onClick={() => handleTestConnection('duitku')}
              variant="outline"
              size="sm"
              className="w-full mt-2 border-emerald-300 text-emerald-600 hover:bg-emerald-50 hover:border-emerald-400 dark:text-emerald-400 dark:hover:bg-emerald-900/30"
            >
              <TestTube className="w-4 h-4 mr-2" />
              Test Connection
            </Button>
          </CardContent>
        </Card>

        {/* TriPay Configuration */}
        <Card className="border-2 border-indigo-200 dark:border-indigo-800 overflow-hidden" data-testid="tripay-config-card">
          <CardHeader className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-white/20 backdrop-blur rounded-xl">
                  <CreditCard className="w-7 h-7 text-white" />
                </div>
                <div>
                  <CardTitle className="text-white text-xl">TriPay</CardTitle>
                  <p className="text-indigo-100 text-xs mt-0.5">Payment Gateway Indonesia</p>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <Label>Merchant Code</Label>
              <Input
                type="text"
                data-testid="tripay-merchant-code"
                value={config.tripay.merchant_code}
                onChange={(e) => setConfig({ ...config, tripay: { ...config.tripay, merchant_code: e.target.value }})}
                placeholder="T12345"
              />
            </div>
            <div>
              <Label>API Key</Label>
              <div className="relative">
                <Input
                  type={showTripayKey ? 'text' : 'password'}
                  data-testid="tripay-api-key"
                  value={config.tripay.api_key}
                  onChange={(e) => setConfig({ ...config, tripay: { ...config.tripay, api_key: e.target.value }})}
                  placeholder="DEV-XXXX or PROD-XXXX"
                />
                <button type="button" onClick={() => setShowTripayKey(!showTripayKey)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500">
                  {showTripayKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Private Key</Label>
              <div className="relative">
                <Input
                  type={showTripayPriv ? 'text' : 'password'}
                  data-testid="tripay-private-key"
                  value={config.tripay.private_key}
                  onChange={(e) => setConfig({ ...config, tripay: { ...config.tripay, private_key: e.target.value }})}
                  placeholder="Private key untuk signing signature"
                />
                <button type="button" onClick={() => setShowTripayPriv(!showTripayPriv)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500">
                  {showTripayPriv ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Digunakan untuk HMAC signature transaksi + verifikasi callback.</p>
            </div>
            <div>
              <Label>Environment</Label>
              <select
                data-testid="tripay-environment"
                value={config.tripay.environment}
                onChange={(e) => setConfig({ ...config, tripay: { ...config.tripay, environment: e.target.value }})}
                className="w-full h-10 px-3 py-2 border rounded-md bg-white dark:bg-slate-800 dark:border-slate-700"
              >
                <option value="sandbox">Sandbox (Testing)</option>
                <option value="production">Production (Live Mode)</option>
              </select>
            </div>
            <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg text-xs text-indigo-700 dark:text-indigo-300 space-y-1">
              <p><strong>Callback URL:</strong></p>
              <code className="block bg-white dark:bg-slate-800 p-2 rounded text-[10px] break-all">
                {(process.env.REACT_APP_BACKEND_URL || window.location.origin) + '/api/v2/payments/tripay/callback'}
              </code>
              <p className="mt-2">Daftar URL di TriPay Dashboard menu Merchant &gt; Opsi &gt; Edit.</p>
            </div>
            <Button
              onClick={() => handleTestConnection('tripay')}
              variant="outline"
              size="sm"
              className="w-full mt-2 border-indigo-300 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-400 dark:text-indigo-400 dark:hover:bg-indigo-900/30"
              data-testid="tripay-test-btn"
            >
              <TestTube className="w-4 h-4 mr-2" />
              Test Connection
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="border-2 border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 dark:border-slate-700">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-blue-100 rounded-xl dark:bg-blue-900/40">
              <CheckCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="space-y-3 flex-1">
              <p className="font-semibold text-slate-800 dark:text-white">Cara Menggunakan Payment Gateway:</p>
              <ol className="list-decimal list-inside space-y-2 text-sm text-slate-600 dark:text-slate-300">
                <li>Daftar akun di <span className="font-medium text-blue-600 dark:text-blue-400">Xendit</span> atau <span className="font-medium text-emerald-600 dark:text-emerald-400">Duitku</span></li>
                <li>Dapatkan API Key / Merchant Code dari dashboard masing-masing provider</li>
                <li>Masukkan credentials di form di atas</li>
                <li>Klik <span className="font-medium">"Test Connection"</span> untuk memverifikasi</li>
                <li>Simpan konfigurasi</li>
                <li>Customer bisa bayar invoice via payment gateway di halaman detail invoice</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentGatewaySettings;
